import {UI} from "../../../../../../../stem-core/src/ui/UIBase";

import {BlinkInput} from "../../../../../../ui/Input";
import {KeyEvent} from "../../../../../../KeyEvent";
import {StemDate} from "../../../../../../../stem-core/src/time/Date";
import {Messages} from "../../../../../../Messages";
import {queueMicrotaskWrapper} from "../../../../../../AsyncAggregateDispatcher";
import {registerStyle} from "../../../../../../../stem-core/src/ui/style/Theme";
import {NOOP_FUNCTION} from "../../../../../../../blink-sdk/utils/Utils";
import {StripeCardInputStyle} from "./CardInput";
import {iFrameMerchantService} from "../../../../../../services/IframeMerchantService";
import {TNR_ALIAS} from "../../../../../../../blink-sdk/Constants";


class CardNumberInput extends BlinkInput {
    acceptedCreditCards = {
        visa: /^4[0-9]{12}(?:[0-9]{3})?$/,
        mastercard: /^5[1-5][0-9]{14}$|^2(?:2(?:2[1-9]|[3-9][0-9])|[3-6][0-9][0-9]|7(?:[01][0-9]|20))[0-9]{12}$/,
        amex: /^3[47][0-9]{13}$/,
        discover: /^65[4-9][0-9]{13}|64[4-9][0-9]{13}|6011[0-9]{12}|(622(?:12[6-9]|1[3-9][0-9]|[2-8][0-9][0-9]|9[01][0-9]|92[0-5])[0-9]{10})$/,
    };

    getDefaultOptions() {
        return {
            ...super.getDefaultOptions(),
            inputAttributes: {
                placeholder: Messages.cardNumberInputPlaceholder,
            }
        };
    }

    validateInput() {
        // remove all non digit characters
        const value = this.getValue();
        let sum = 0;
        let shouldDouble = false;
        // loop through values starting at the rightmost side
        for (let i = value.length - 1; i >= 0; i--) {
            let digit = parseInt(value.charAt(i), 10);

            if (shouldDouble) {
                if ((digit *= 2) > 9) digit -= 9;
            }

            sum += digit;
            shouldDouble = !shouldDouble;
        }

        let valid = (sum % 10) === 0;
        let accepted = false;

        // Loop through the keys (visa, mastercard, amex, etc.)
        Object.keys(this.acceptedCreditCards).forEach ((key) => {
            if (key === "discover" && iFrameMerchantService.isMerchant(TNR_ALIAS)) {
                return;
            }
            let cardRegex = this.acceptedCreditCards[key];
            if (cardRegex.test(value)) {
                accepted = true;
                this.dispatch("cardTypeIdentified", key);
            }
        });

        if (valid && accepted) {
            return true;
        }

        return valid && accepted;
    }

    getValue() {
        return this.input.getValue().split(" ").join("");
    }

    setValue(value) {
        let insertedValue = "";
        for (const ch of [...value]) {
            if (insertedValue.length % 5 === 4) {
                insertedValue += " ";
            }
            insertedValue += ch;
        }
        this.input.setValue(insertedValue);
    }

    initValidationListeners() {
        const inputPattern = new RegExp("^[0-9]{0,16}$");

        this.input.addNodeListener("keydown", e => {
            const event = new KeyEvent(e);
            const v = this.input.getValue();
            this.saveSelection();
            const applyCustomValue = value => {
                this.setValue(value.split(" ").join(""));
                this.input.node.setSelectionRange(this.selectionStart, this.selectionStart);
                this.saveSelection();
                e.preventDefault();
                e.stopPropagation();
            };

            if (event.isBackspace() && this.selectionStart % 5 === 0 && this.selectionStart === this.selectionEnd) {
                const value = v.substr(0, this.selectionStart - 2) + v.substr(this.selectionStart);
                this.selectionStart -= 2;
                applyCustomValue(value);
            }
            if (event.isDelete() && this.selectionStart % 5 === 4 && this.selectionStart === this.selectionEnd) {
                const value = v.substr(0, this.selectionStart) + v.substr(this.selectionStart + 2);
                this.selectionStart += 1;
                applyCustomValue(value);
            }
        })

        this.input.addNodeListener("input", () => {
            const insertedValue = this.getValue();
            let currentLength = this.input.getValue().length;
            if (!insertedValue.match(inputPattern)) {
                this.setValue([...insertedValue].filter(ch => ch.match(/\d/)).slice(0,16).join(""));
                if (insertedValue.length === 17) {
                    if (this.selectionStart % 5 === 4) {
                        this.selectionStart += 2;
                    } else {
                        this.selectionStart += 1;
                    }
                }
                this.input.node.setSelectionRange(this.selectionStart, this.selectionStart);
                return;
            }
            const {selectionStart, selectionEnd} = this.input.node;
            this.setValue(insertedValue);
            if (this.input.getValue().length - currentLength === 1 && this.selectionStart === currentLength - 1) {
                this.selectionStart += 2;
                this.input.node.setSelectionRange(this.selectionStart, this.selectionStart);
            } else {
                this.input.node.setSelectionRange(selectionStart, selectionEnd);
            }
            this.options.onValidInput(insertedValue);
            this.saveSelection();
            this.clearError();
        });
    }
}

class ExpiryDateInput extends BlinkInput {
    getDefaultOptions() {
        return {
            ...super.getDefaultOptions(),
            inputAttributes: {
                placeholder: Messages.cardDateInputPlaceholder,
            },
            onFocusPreviousField: NOOP_FUNCTION
        };
    }

    getValue() {
        return this.input.getValue().replace("/", "");
    }

    validateInput() {
        if (!this.input.getValue().match(/^(((0[1-9])|(1[0-2]))\/([0-9]{2}))$/)) {
            return false;
        }
        const inputDate = new StemDate(this.input.getValue().substr(0,2) + "/02/" + this.input.getValue().substr(3,2));
        return inputDate && StemDate.now().getStartOfMonth().isBefore(inputDate);
    }

    initKeyListeners() {
        super.initKeyListeners();

        this.input.addNodeListener("keyup", event => {
            const keyEvent = new KeyEvent(event);
            if (keyEvent.isBackspace() && !this.selectionStart) {
                queueMicrotaskWrapper(this.options.onFocusPreviousField);
            }
        });
    }

    initValidationListeners() {
        let value = this.input.getValue();
        this.input.addNodeListener("keydown", event => {
            const keyEvent = new KeyEvent(event);
            if (keyEvent.isBackspace()) {
                if (this.selectionStart <= 3) {
                    this.input.setValue("");
                    event.preventDefault();
                    event.stopPropagation();
                }
            }
        });

        this.input.addNodeListener("input", () => {
            let insertedValue = this.input.getValue();

            const modifyValue = (newValue, updateSelection=true) => {
                if (!updateSelection) {
                    this.selectionStart = this.input.node.selectionStart;
                } else {
                    this.selectionStart = newValue.length;
                }
                value = newValue;
                this.input.setValue(newValue);
                this.input.node.setSelectionRange(this.selectionStart, this.selectionStart);
                this.saveSelection();
            };

            if (insertedValue.match(/^[0-1]$/)) {
                modifyValue(insertedValue);
                return;
            }
            if (insertedValue.match(/^0[1-9]$/)) {
                modifyValue(insertedValue + "/");
                return;
            }
            if (insertedValue.match(/^[2-9]$/)) {
                modifyValue("0" + insertedValue + "/");
                return;
            }
            if (insertedValue.match(/^1[0-2]$/)) {
                modifyValue(insertedValue + "/");
                return;
            }
            if (insertedValue.match(/^1\/$/)) {
                modifyValue("0" + insertedValue);
                return;
            }
            const partialMatch = insertedValue.match(/^(((0[1-9])|(1[0-2]))\/([0-9]{0,2}))/)?.[0];
            if (partialMatch) {
                modifyValue(partialMatch, false);
                return;
            }
            this.input.setValue(value);
            this.saveSelection();
            this.clearError();
        });
    }
}

class CVCInput extends BlinkInput {
    getDefaultOptions() {
        return {
            ...super.getDefaultOptions(),
            inputAttributes: {
                placeholder: Messages.cardCVCInputPlaceholder,
            },
            onFocusPreviousField: NOOP_FUNCTION,
            amexCard: false,
        };
    }

    setOptions(options) {
        if (options.amexCard) {
            options.inputPattern = /^[0-9]{0,4}$/;
        } else {
            options.inputPattern = /^[0-9]{0,3}$/;
        }
        super.setOptions(options);
    }

    validateInput() {
        if (this.options.amexCard) {
            return this.getValue().match(/^[0-9]{4}$/);
        }
        return this.getValue().match(/^[0-9]{3}$/);
    }

    initKeyListeners() {
        super.initKeyListeners();

        this.input.addNodeListener("keyup", event => {
            const keyEvent = new KeyEvent(event);
            if (keyEvent.isBackspace() && !this.selectionStart) {
                queueMicrotaskWrapper(this.options.onFocusPreviousField);
            }
        });
    }
}

@registerStyle(StripeCardInputStyle)
export class BlinkCardInput extends UI.Element {
    focused = false;
    amexCard = false;

    getDefaultOptions() {
        return {
            ...super.getDefaultOptions(),
            onChange: NOOP_FUNCTION,
            label: "addCard",
            autofocus: true,
        };
    }

    extraNodeAttributes(attr) {
        super.extraNodeAttributes(attr);
        attr.addClass(this.styleSheet.element);
    }

    constructor(...args) {
        super(...args);
        this.validInputs = false;
    }

    confirmCard(callback = NOOP_FUNCTION, errorCallback = NOOP_FUNCTION) {
        if (!this.isInputComplete()) {
            errorCallback();
        }

        callback({
            cardNumber: this.cardNumberInput.getValue(),
            cardExpiry: this.cardExpiryInput.getValue(),
            cardCvv: this.cvvInput.getValue(),
        });
    }

    confirmCardWithoutStoring(scope, callback = NOOP_FUNCTION, errorCallback = NOOP_FUNCTION) {
        this.confirmCard(callback, errorCallback);
    }

    hasValidSecureFields() {
        return this.validInputs;
    }

    isInputComplete() {
        return this.cardNumberInput.isValid() &&
            this.cardExpiryInput.isValid() &&
            this.cvvInput.isValid();
    }

    isLoaded() {
        return true;
    }

    render() {
        const {styleSheet} = this;
        const {success} = this.options;
        const inputContainerClasses = `${styleSheet.inputContainer} ${
            success ? styleSheet.success : this.error ? styleSheet.error : ""
        } ${this.focused ? styleSheet.focused : ""}`;

        return [
            <div className={inputContainerClasses}>
                <div
                    style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                    }}>
                    <CardNumberInput ref="cardNumberInput"
                                    style={{flex: 0.6, marginTop: 0}}
                                    onBlur={() => this.cardNumberInput.validate()}
                                    onChange={() => {
                                 if (this.cardNumberInput.isValid()) {
                                     this.cardExpiryInput.focus();
                                 }
                                 this.options.onChange();
                             }}
                                    validators={[{
                                 condition: () => this.cardNumberInput.validateInput(),
                             }]}
                    />
                    <ExpiryDateInput ref="cardExpiryInput"
                                     style={{flex: 0.25, marginTop: 0}}
                                     onBlur={() => this.cardExpiryInput.validate()}
                                     onChange={() => {
                                         if (this.cardExpiryInput.isValid()) {
                                             this.cvvInput.focus();
                                         }
                                         this.options.onChange();
                                     }}
                                     onFocusPreviousField={() => this.cardNumberInput.focus()}
                                     validators={[{
                                         condition: () => this.cardExpiryInput.validateInput(),
                                     }]}
                    />
                    <CVCInput ref="cvvInput" amexCard={this.amexCard}
                              style={{flex: 0.2, marginTop: 0}}
                              onBlur={() => this.cvvInput.validate()}
                              onFocusPreviousField={() => this.cardExpiryInput.focus()}
                              onChange={() => this.options.onChange()}
                              validators={[{
                                  condition: () => this.cvvInput.validateInput(),
                              }]}
                    />
                </div>
                <div className={styleSheet.successMessage}>{success}</div>
            </div>,
            this.error ? <div testId="cardInputError" className={styleSheet.errorMessage}>{this.error}</div> : <div/>,
        ];
    }

    onMount() {
        super.onMount();

        this.cardNumberInput.addListener("cardTypeIdentified", key => {
            this.amexCard = key === "amex";
            this.redraw();
        })
    }
}
